import * as React from 'react';
import { Fragment } from 'react';
import { observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Icon from './Icon';
import Translate from './Translate';
import { ShowChange } from '../models/configuration/ShowChange';
import { DataStore } from '../stores';

const Value = styled<any, any>('div')`
    ${propStyles};
`;

Value.defaultProps = {
    textDecoration: 'inherit',
};

interface ChangeIndicatorProps {
    dataStore: DataStore;
    theme?: { [property: string]: string | number };
    value: string | number;
    separator?: string;
    alignIcon?: 'right' | 'left' | null;
    upIcon?: string;
    downIcon?: string;
    noChangeIcon?: string;
    showChange?: ShowChange;
}

interface ChangeIndicatorValueProps {
    changeTheme: string | number;
    color: string;
    theme?: { [property: string]: string | number };
    value: string | number;
}

interface ChangeIndicatorIconProps {
    theme?: { [property: string]: string | number };
    filename: string;
    changeTheme: string | number;
}

const iconStyleProps = {
    display: 'inline-block',
    verticalAlign: 'middle',
};

export const ChangeIndicatorValue: React.SFC<ChangeIndicatorValueProps> = ({
    changeTheme,
    color,
    theme,
    value,
}) => {
    return (
        <Value
            color={color}
            {...changeTheme}
            {...theme.ChangeValue}
            {...iconStyleProps}
        >
            <Translate>{value}</Translate>
        </Value>
    );
};

export const ChangeIndicatorIcon: React.SFC<ChangeIndicatorIconProps> = ({
    theme,
    filename,
    changeTheme,
}) => {
    return (
        <Icon
            fileName={filename}
            {...changeTheme}
            {...iconStyleProps}
            {...theme.ChangeIcon}
        />
    );
};

export const ChangeIndicator: React.SFC<ChangeIndicatorProps> = ({
    dataStore,
    theme,
    value,
    separator,
    alignIcon = 'right',
    upIcon = 'ChangeUpIcon',
    downIcon = 'ChangeDownIcon',
    noChangeIcon = 'NoChangeIcon',
    showChange,
}) => {
    // Depending on format codes used values maybe a string or number, e.g. "-4.2%" or -4.2
    // So if the value is not a number strip of any non-numeric characters (except "." & "-") then parseFloat.
    const valueAsNumber =
        isNaN(value as number) && value !== '-'
            ? parseFloat(`${value}`.replace(/[^0-9.-]/g, ''))
            : value;
    showChange = showChange || dataStore.defaultShowChange;

    const {
        showIcon = true,
        showValue = true,
        changeUpColor,
        changeDownColor,
    } = showChange;

    let changeTheme = theme.NoChangeIcon;
    let color = null;
    let filename = noChangeIcon;

    if (valueAsNumber > 0) {
        changeTheme = theme.ChangeValueUp;
        color = changeUpColor;
        filename = upIcon;
    } else if (valueAsNumber < 0) {
        changeTheme = theme.ChangeValueDown;
        color = changeDownColor;
        filename = downIcon;
    }

    const changeIndicatorValue = showValue && (
        <ChangeIndicatorValue
            changeTheme={changeTheme}
            color={color}
            theme={theme}
            value={value}
        />
    );

    const changeIndicatorIcon = showIcon && (
        <ChangeIndicatorIcon
            theme={theme}
            filename={filename}
            changeTheme={changeTheme}
        />
    );

    return (
        <Fragment>
            {alignIcon === 'left' && changeIndicatorIcon}
            {changeIndicatorValue}
            {(!alignIcon || alignIcon === 'right') && changeIndicatorIcon}
            {separator}
        </Fragment>
    );
};

export default observer(withTheme(ChangeIndicator));



// WEBPACK FOOTER //
// ./src/Components/ChangeIndicator.tsx